// notification.service.js - Service for sending SMS and Email notifications
import { generateClient } from 'aws-amplify/api';
import { getAuth0Token } from './auth.service';

// Initialize GraphQL client
const client = generateClient();


/**
 * Send an SMS message with a video link to both the submission phone number and additional recipients
 * 
 * @param {string} submissionNumber - The submission's mobile number (will receive the SMS)
 * @param {string|Array} recipientNumbers - Additional recipient phone number(s) - can be string (comma-delimited) or array
 * @param {string} message - The message to send including the video link
 * @returns {Promise} - Resolves when the SMS is sent
 */
export const sendVideoSMS = async (submissionNumber, recipientNumbers, message) => {
  try {
    // Get access token via the secure proxy
    const token = await getAuth0Token();
    
    // Use environment variable for API endpoint
    const apiUrl = process.env.REACT_APP_API_GATEWAY_URL + "sms/send";
    
    // Format recipient phone numbers as an array
    let recipients = [];
    
    // Add submission number to recipients if provided
    if (submissionNumber) {
      recipients.push(formatPhoneNumber(submissionNumber));
    }
    
    // Process additional recipients (could be comma-delimited string or array)
    if (recipientNumbers) {
      if (Array.isArray(recipientNumbers)) {
        // Format each number in the array
        const formattedNumbers = recipientNumbers.map(num => formatPhoneNumber(num));
        recipients = [...recipients, ...formattedNumbers];
      } else if (typeof recipientNumbers === 'string') {
        // Split comma-delimited string and format each number
        const additionalRecipients = recipientNumbers
          .split(',')
          .map(num => formatPhoneNumber(num.trim()));
        recipients = [...recipients, ...additionalRecipients];
      }
    }
    
    // Remove any empty entries and filter out duplicates
    recipients = [...new Set(recipients.filter(num => num && num.trim() !== ''))];
    
    // Call the SMS API with the access token
    const response = await fetch(apiUrl, {
      method: 'POST',
      mode: 'cors', // ✅ Ensure the request follows CORS rules
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify({
        recipients: recipients,
        message: message
      }),
    });
    
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Failed to send SMS');
    }
    
    const result = await response.json();
    await logSMSActivity(submissionNumber, recipients, message);
    return result;
  } catch (error) {
    console.error('Error sending SMS:', error);
    throw error;
  }
};

/**
 *  Send a message to a conversation using Twilio Conversations API
 * 
 * @param {string} conversationSid - The conversation SID to send the message to
 * @param {string} author - The author of the message
 * @param {string} messageBody - The message body to send
 * @returns {Promise} - Resolves when the SMS is sent
 */
export const sendConversationMessage = async (conversationSid, messageBody, author, media = null) => {
  try {  
    const token = await getAuth0Token();
    const apiUrl = `${process.env.REACT_APP_API_GATEWAY_URL}conversations/messages`;
    
    const payload = { conversationSid, author, body: messageBody };
    
    // Add media if provided (base64 data URI string)
    if (media) {
      payload.media = media;
    }

    const response = await fetch(apiUrl, {
      method: 'POST',
      mode: 'cors',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify(payload)
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Failed to send conversation message');
    }

    const result = await response.json();
    await logSMSActivity(author, messageBody);
    return result;    
  } catch (error) {
    console.error('Error sending SMS:', error);
    throw error;
  }    
};

/**
 * Send an email with a video link to both the submission email and additional recipients
 * 
 * @param {string|Array} recipientEmails - Additional recipient email(s) - can be string (comma-delimited) or array
 * @param {string} subject - Email subject line
 * @param {string} message - The message to send including the video link
 * @param {string} videoLink - The video session link to include
 * @returns {Promise} - Resolves when the email is sent
 */
export const sendVideoEmail = async (recipientEmails, subject, message, videoLink) => {
  try {
    // Get access token via the secure proxy
    const token = await getAuth0Token();
    
    // Use environment variable for API endpoint
    const apiUrl = process.env.REACT_APP_API_GATEWAY_URL + "email/send";;
    
    // Format recipient emails as an array
    let recipients = [];
    
    // Process additional recipients (could be comma-delimited string or array)
    if (recipientEmails) {
      if (Array.isArray(recipientEmails)) {
        recipients = [...recipients, ...recipientEmails];
      } else if (typeof recipientEmails === 'string') {
        const additionalRecipients = recipientEmails.split(',').map(email => email.trim());
        recipients = [...recipients, ...additionalRecipients];
      }
    }
    
    // Remove any empty entries and filter out duplicates
    recipients = [...new Set(recipients.filter(email => email && email.trim() !== ''))];
    
    // Call the Email API with the access token
    const response = await fetch(apiUrl, {
      method: 'POST',
      mode: 'cors', // ✅ Ensure the request follows CORS rules
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify({
        emailList: recipients.join(','),
        subject: subject || 'Your Video Session Link',
        customMessage: message || '',
        videoLink: videoLink
      }),
    });
    
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Failed to send email');
    }
    
    const result = await response.json();
    
    // Log the email activity
    await logEmailActivity(recipientEmails, subject, message,videoLink);
    
    return result;
  } catch (error) {
    console.error('Error sending email:', error);
    throw error;
  }
};

export const sendAppointmentNotification = async ({ mobileNumber, slotDate, startTime, variantTrack = null, submissionId = null }) => {
  try {
    const token = await getAuth0Token();
    const apiUrl = `${process.env.REACT_APP_API_GATEWAY_URL}sms/sendappt`;

    console.log('📤 Sending appointment notification with payload:', {
      mobileNumber,
      slotDate,
      startTime,
      variantTrack,
      submissionId
    });

    console.log('🔐 Using token:', token ? '[REDACTED]' : 'None');

    const response = await fetch(apiUrl, {
      method: 'POST',
      mode: 'cors',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      },
      body: JSON.stringify({
        mobileNumber,
        slotDate,
        startTime,
        variantTrack,
        submissionId
      })
    });

    console.log(`📡 Request sent to ${apiUrl}. Status:`, response.status);

    if (!response.ok) {
      const errorData = await response.json();
      console.error('❌ Response error body:', errorData);
      throw new Error(errorData.error || 'Failed to send appointment notification');
    }

    const result = await response.json();
    console.log('✅ Appointment notification sent successfully:', result);
    return result;
  } catch (error) {
    console.error('❌ Error sending appointment notification:', error);
    throw error;
  }
};


/**
 * Format a phone number to E.164 format for SMS sending
 * 
 * @param {string} phoneNumber - The phone number to format
 * @returns {string} - The formatted phone number
 */
export const formatPhoneNumber = (phoneNumber) => {
  // Remove all non-numeric characters
  const cleaned = ('' + phoneNumber).replace(/\D/g, '');
  
  // Add US country code if it's a 10-digit number
  if (cleaned.length === 10) {
    return `+1${cleaned}`;
  } else if (cleaned.length === 11 && cleaned.charAt(0) === '1') {
    return `+${cleaned}`;
  }
  
  // Return whatever we have if it doesn't match expected patterns
  return `+${cleaned}`;
};

/**
 * Validate an email address format
 * 
 * @param {string} email - The email to validate
 * @returns {boolean} - Whether the email appears valid
 */
export const validateEmail = (email) => {
  // Basic email validation regex
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

/**
 * Log SMS activity
 * 
 * @param {string} submissionNumber - The submission's number
 * @param {string|Array} recipientNumbers - The recipient number(s)
 * @param {string} message - The message that was sent
 */
const logSMSActivity = async (submissionNumber, recipientNumbers, message) => {
  try {
    // Format recipients for logging
    let recipients = '';
    if (Array.isArray(recipientNumbers)) {
      recipients = recipientNumbers.join(', ');
    } else {
      recipients = recipientNumbers;
    }
    
    // Here you would typically log the SMS activity to your database
    console.log(`Logging SMS activity from ${submissionNumber} to recipients: ${recipients}`);
    
    // This would be implemented when you have the appropriate API
  } catch (error) {
    console.error('Error logging SMS activity:', error);
    // Don't throw here, as this is a non-critical operation
  }
};

/**
 * Log email activity
 * 
 * @param {string} submissionEmail - The submission's email
 * @param {string|Array} recipientEmails - The recipient email(s)
 * @param {string} subject - The email subject
 * @param {string} message - The message that was sent
 */
const logEmailActivity = async (submissionEmail, recipientEmails, subject, message) => {
  try {
    // Here you would typically log the email activity to your database
    console.log(`Logging email activity to ${submissionEmail} and additional recipients`);
    
    // This would be implemented when you have the appropriate API
  } catch (error) {
    console.error('Error logging email activity:', error);
    // Don't throw here, as this is a non-critical operation
  }
};

/**
 * Send notifications via both SMS and email if available
 * 
 * @param {Object} options - Notification options
 * @param {string} options.submissionPhone - The submission's phone number
 * @param {string|Array} options.recipientPhones - Additional recipient phone number(s)
 * @param {string} options.submissionEmail - The submission's email
 * @param {string|Array} options.recipientEmails - Additional recipient email(s)
 * @param {string} options.smsMessage - Message to send via SMS
 * @param {string} options.emailSubject - Email subject line
 * @param {string} options.emailMessage - Message to send via email
 * @param {string} options.videoLink - Video session link
 * @returns {Promise} - Resolves when notifications are sent
 */
export const sendMultiChannelNotification = async (options) => {
  const {
    submissionPhone,
    recipientPhones,
    submissionEmail,
    recipientEmails,
    smsMessage,
    emailSubject,
    emailMessage,
    videoLink
  } = options;
  
  const results = {
    sms: null,
    email: null
  };
  
  const promises = [];
  
  // Send SMS if phone numbers are provided
  if (submissionPhone || recipientPhones) {
    const smsPromise = sendVideoSMS(
      submissionPhone,
      recipientPhones,
      smsMessage
    ).then(result => {
      results.sms = { success: true, result };
      return result;
    }).catch(error => {
      results.sms = { success: false, error: error.message };
      // Don't re-throw to allow email to still be sent even if SMS fails
    });
    
    promises.push(smsPromise);
  }
  
  // Send email if email addresses are provided
  if (submissionEmail || (recipientEmails && (
      (typeof recipientEmails === 'string' && recipientEmails.trim() !== '') ||
      (Array.isArray(recipientEmails) && recipientEmails.length > 0)
    ))) {
    
    const emailPromise = sendVideoEmail(
      submissionEmail,
      recipientEmails,
      emailSubject,
      emailMessage,
      videoLink
    ).then(result => {
      results.email = { success: true, result };
      return result;
    }).catch(error => {
      results.email = { success: false, error: error.message };
      // Don't re-throw to allow partial success reporting
    });
    
    promises.push(emailPromise);
  }
  
  // Wait for all notifications to complete
  await Promise.all(promises);
  
  return results;
};

/**
 * Clear the awaitingAppointmentConfirmation flag on a conversation
 * Called when an appointment is canceled
 * 
 * @param {string} submissionId - The submission ID to clear the flag for
 * @returns {Promise} - Resolves when the flag is cleared
 */
export const clearAppointmentConfirmationFlag = async (submissionId) => {
  try {
    const token = await getAuth0Token();
    const apiUrl = `${process.env.REACT_APP_API_GATEWAY_URL}sms/clearAppointmentFlag`;

    console.log('🔄 Clearing appointment confirmation flag for submission:', submissionId);

    const response = await fetch(apiUrl, {
      method: 'POST',
      mode: 'cors',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      },
      body: JSON.stringify({ submissionId })
    });

    if (!response.ok) {
      const errorData = await response.json();
      console.error('❌ Failed to clear appointment confirmation flag:', errorData);
      throw new Error(errorData.error || 'Failed to clear appointment confirmation flag');
    }

    const result = await response.json();
    console.log('✅ Appointment confirmation flag cleared:', result);
    return result;
  } catch (error) {
    console.error('❌ Error clearing appointment confirmation flag:', error);
    throw error;
  }
};